import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWallet, faCreditCard, faExchangeAlt, faChartLine, faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import { ParallaxProvider } from 'react-scroll-parallax';
import AOS from 'aos';
import 'aos/dist/aos.css';

// Import components
import ParallaxElements from './components/ParallaxElements';
import ScrollToTopButton from './components/ScrollToTopButton';

// Import pages
import Home from './pages/Home';
import Features from './pages/Features';
import Contact from './pages/Contact';

// ScrollToTop component
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [pathname]);

  return null;
};

// Main App wrapper
const AppWrapper = () => {
  return (
    <Router>
      <ScrollToTop />
      <App />
    </Router>
  );
};

function App() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
    });

    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { text: 'Features', path: '/features' },
    { text: 'Contact', path: '/contact' },
  ];

  return (
    <ParallaxProvider>
      <div className="min-h-screen bg-white relative overflow-hidden">
        {/* Parallax Elements */}
        <ParallaxElements />
        
        {/* Scroll to Top Button */}
        <ScrollToTopButton />
        
        {/* Gradient Background */}
        <div className="absolute inset-0 z-0">
          <div 
            className="absolute inset-0 opacity-[0.15]"
            style={{
              backgroundImage: `
                linear-gradient(to right, rgba(59, 130, 246, 0.1) 1px, transparent 1px),
                linear-gradient(to bottom, rgba(59, 130, 246, 0.1) 1px, transparent 1px)
              `,
              backgroundSize: '50px 50px'
            }}
          />
          
          {/* Radial Gradient Overlay */}
          <div 
            className="absolute inset-0"
            style={{
              background: 'radial-gradient(circle at 50% 50%, rgba(59, 130, 246, 0.1) 0%, rgba(255, 255, 255, 0) 50%)'
            }}
          />
          
          {/* Animated Glow Effects */}
          <div 
            className="absolute -top-40 -left-40 w-80 h-80 bg-blue-200 rounded-full filter blur-[100px] opacity-20 animate-pulse"
            style={{ animationDuration: '4s' }}
          />
          <div 
            className="absolute -bottom-40 -right-40 w-80 h-80 bg-green-200 rounded-full filter blur-[100px] opacity-20 animate-pulse"
            style={{ animationDuration: '6s' }}
          />
        </div>

        {/* Content */}
        <div className="relative z-10">
          <nav
            className={`fixed w-full z-50 transition-all duration-300 ${
              scrolled
                ? 'bg-white/90 backdrop-blur-md shadow-lg shadow-blue-100'
                : 'bg-transparent'
            }`}
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex justify-between items-center h-20">
                <Link
                  to="/"
                  className="flex items-center space-x-3 text-blue-500 hover:text-green-500 transition-all duration-300 transform hover:scale-105"
                >
                  <span className="font-extrabold text-2xl tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-blue-500 via-green-500 to-amber-500">UniPay</span>
                </Link>

                {/* Desktop Navigation */}
                <div className="hidden md:flex items-center space-x-10">
                  {navItems.map((item) => (
                    <Link
                      key={item.text}
                      to={item.path}
                      className="text-gray-700 hover:text-blue-500 transition-all duration-300 relative group text-sm font-medium"
                    >
                      {item.text}
                      <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-blue-500 transition-all duration-300 group-hover:w-full"></span>
                    </Link>
                  ))}
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="bg-gradient-to-r from-blue-500 to-green-500 text-white px-6 py-2.5 rounded-lg font-semibold flex items-center space-x-2 hover:shadow-lg hover:shadow-blue-200 transition-all duration-300 text-sm cursor-pointer"
                  >
                    <FontAwesomeIcon icon={faWallet} className="text-lg" />
                    <span>Connect Wallet</span>
                  </motion.button>
                </div>

                {/* Mobile menu button */}
                <div className="md:hidden">
                  <button
                    onClick={() => setIsOpen(!isOpen)}
                    className="text-gray-700 hover:text-blue-500 p-2 transition-colors duration-300"
                    aria-label="Toggle menu"
                  >
                    <motion.div
                      animate={isOpen ? "open" : "closed"}
                      className="w-6 h-6 relative"
                    >
                      <span className={`absolute h-0.5 w-6 bg-current transform transition-all duration-300 ${isOpen ? 'rotate-45 translate-y-2.5' : ''}`} />
                      <span className={`absolute h-0.5 w-6 bg-current transform transition-all duration-300 translate-y-2 ${isOpen ? 'opacity-0' : ''}`} />
                      <span className={`absolute h-0.5 w-6 bg-current transform transition-all duration-300 translate-y-4 ${isOpen ? '-rotate-45 -translate-y-0.5' : ''}`} />
                    </motion.div>
                  </button>
                </div>
              </div>
            </div>

            {/* Mobile menu */}
            <AnimatePresence>
              {isOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="md:hidden bg-white/95 backdrop-blur-lg shadow-lg"
                >
                  <div className="px-4 pt-2 pb-6 space-y-4">
                    {navItems.map((item) => (
                      <Link
                        key={item.text}
                        to={item.path}
                        className="block py-3 text-gray-700 hover:text-blue-500 transition-colors duration-300 text-sm font-medium"
                        onClick={() => setIsOpen(false)}
                      >
                        {item.text}
                      </Link>
                    ))}
                    <motion.button
                      whileTap={{ scale: 0.95 }}
                      className="w-full bg-gradient-to-r from-blue-500 to-green-500 text-white px-4 py-3 rounded-lg font-semibold flex items-center justify-center space-x-2 hover:shadow-lg hover:shadow-blue-200 transition-all duration-300 text-sm"
                    >
                      <FontAwesomeIcon icon={faWallet} className="text-lg" />
                      <span>Connect Wallet</span>
                    </motion.button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </nav>

          <main className="">
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/features" element={<Features />} />
              <Route path="/contact" element={<Contact />} />
            </Routes>
          </main>

          <footer className="bg-gray-50 mt-auto">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8">
                <div>
                  <h3 className="text-blue-500 font-semibold text-lg mb-4">
                    UniPay
                  </h3>
                  <p className="text-gray-600">Bridging Web3 to Web2 Payments</p>
                </div>
                <div>
                  <h4 className="text-gray-800 font-semibold mb-4">Quick Links</h4>
                  <div className="flex flex-col space-y-2">
                    {navItems.map((item) => (
                      <Link
                        key={item.text}
                        to={item.path}
                        className="text-gray-600 hover:text-blue-500 transition-colors"
                      >
                        {item.text}
                      </Link>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="text-gray-800 font-semibold mb-4">Resources</h4>
                  <div className="flex flex-col space-y-2">
                    <a href="#" className="text-gray-600 hover:text-blue-500 transition-colors">Documentation</a>
                    <a href="#" className="text-gray-600 hover:text-blue-500 transition-colors">API Reference</a>
                    <a href="#" className="text-gray-600 hover:text-blue-500 transition-colors">Tutorials</a>
                  </div>
                </div>
                <div>
                  <h4 className="text-gray-800 font-semibold mb-4">Legal</h4>
                  <div className="flex flex-col space-y-2">
                    <a href="#" className="text-gray-600 hover:text-blue-500 transition-colors">Privacy Policy</a>
                    <a href="#" className="text-gray-600 hover:text-blue-500 transition-colors">Terms of Service</a>
                    <a href="#" className="text-gray-600 hover:text-blue-500 transition-colors">Compliance</a>
                  </div>
                </div>
              </div>
              <div className="mt-12 pt-8 border-t border-gray-200 text-center text-gray-500 text-sm">
                <p> {new Date().getFullYear()} UniPay. All rights reserved.</p>
              </div>
            </div>
          </footer>
        </div>
      </div>
    </ParallaxProvider>
  );
}

export default AppWrapper;
